import React, { useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Copy, Download, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * Share dialog: shows a QR code for the encoded board URL, the URL itself,
 * and buttons to copy it / download the QR as a PNG.
 */
export default function ShareDialog({ open, onOpenChange, url }) {
  const [copied, setCopied] = useState(false);
  const svgRef = useRef(null);

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt("Copy this share link:", url);
    }
  };

  const downloadQr = () => {
    const svg = svgRef.current;
    if (!svg) return;
    const xml = new XMLSerializer().serializeToString(svg);
    const svg64 = btoa(unescape(encodeURIComponent(xml)));
    const img = new Image();
    img.onload = () => {
      const size = 1024;
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#0A0A0A";
      ctx.fillRect(0, 0, size, size);
      // Center the QR (which is 900x900 with 62px margin each side after scaling)
      const pad = 60;
      ctx.drawImage(img, pad, pad, size - pad * 2, size - pad * 2);
      const a = document.createElement("a");
      a.download = "syd-pid-share-qr.png";
      a.href = canvas.toDataURL("image/png");
      a.click();
      toast.success("QR downloaded");
    };
    img.src = `data:image/svg+xml;base64,${svg64}`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="share-dialog"
        className="rounded-none bg-[#0E0E0E] border border-[#2A2A2A] text-white sm:max-w-md"
      >
        <DialogHeader>
          <DialogTitle className="font-display font-bold tracking-tight text-white">
            Share this board
          </DialogTitle>
          <DialogDescription className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/50">
            Scan the QR or copy the link
          </DialogDescription>
        </DialogHeader>

        {/* QR code panel */}
        <div className="flex justify-center py-2">
          <div
            className="bg-white p-3"
            data-testid="share-qr-container"
          >
            <QRCodeSVG
              ref={svgRef}
              value={url || ""}
              size={220}
              level="M"
              includeMargin={false}
              bgColor="#FFFFFF"
              fgColor="#0A0A0A"
              imageSettings={undefined}
            />
          </div>
        </div>

        {/* URL preview */}
        <div className="border border-[#2A2A2A] bg-[#111] px-3 py-2 font-mono text-[11px] text-white/70 break-all max-h-24 overflow-y-auto">
          {url}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <Button
            onClick={copyUrl}
            data-testid="share-dialog-copy"
            className="rounded-none bg-white text-black hover:bg-white/85 font-mono uppercase tracking-widest text-[11px] flex-1"
          >
            {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
            {copied ? "Copied" : "Copy link"}
          </Button>
          <Button
            variant="outline"
            onClick={downloadQr}
            data-testid="share-dialog-download-qr"
            className="rounded-none border-[#2A2A2A] bg-transparent text-white hover:bg-[#1a1a1a] font-mono uppercase tracking-widest text-[11px]"
          >
            <Download className="w-4 h-4 mr-2" />
            QR PNG
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
